import React, { useState, useEffect } from 'react';
import { Bell, X, Smartphone } from 'lucide-react';
import { Button } from './ui/button';
import { requestNotificationPermission, onForegroundMessage } from '../config/firebase';
import api from '../services/api';
import { toast } from '../hooks/use-toast';

// ============== First-Time Login Modal ==============
export const NotificationPromptModal = ({ isOpen, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleEnable = async () => {
    setIsLoading(true);
    try {
      const token = await requestNotificationPermission();
      if (token) {
        await api.saveFcmToken(token);
        localStorage.setItem('fcm_token', token);
        localStorage.setItem('notification_prompt_shown', 'true');
        toast({
          title: "Notifications Enabled!",
          description: "You'll receive alerts for bookings, cancellations, and payments.",
        });
        onClose(true);
      } else {
        toast({
          title: "Permission Denied",
          description: "You can enable notifications later from the sidebar.",
          variant: "destructive"
        });
        onClose(false);
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
      toast({
        title: "Error",
        description: "Failed to enable notifications. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    localStorage.setItem('notification_prompt_shown', 'true');
    onClose(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md mx-4 overflow-hidden animate-in fade-in zoom-in duration-300">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-[#0A2C93] to-[#1a4fd6] p-6 text-white">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
              <Bell className="h-8 w-8" />
            </div>
          </div>
          <h2 className="text-xl font-bold text-center">Stay Updated!</h2>
          <p className="text-white/80 text-center text-sm mt-1">
            Never miss a booking or payment
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="space-y-3 mb-6">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <span className="text-green-600 text-sm">✓</span>
              </div>
              <div>
                <p className="font-medium text-gray-900">New Bookings</p>
                <p className="text-sm text-gray-500">Instant alerts when customers book</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <span className="text-orange-600 text-sm">✓</span>
              </div>
              <div>
                <p className="font-medium text-gray-900">Cancellations</p>
                <p className="text-sm text-gray-500">Know immediately when slots free up</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <span className="text-blue-600 text-sm">✓</span>
              </div>
              <div>
                <p className="font-medium text-gray-900">Payments</p>
                <p className="text-sm text-gray-500">Real-time payment confirmations</p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <Button
              onClick={handleEnable}
              disabled={isLoading}
              className="w-full bg-[#0A2C93] hover:bg-[#0A2C93]/90 text-white py-6"
            >
              <Bell className="h-5 w-5 mr-2" />
              {isLoading ? 'Enabling...' : 'Enable Notifications'}
            </Button>
            <Button
              onClick={handleSkip}
              variant="ghost"
              className="w-full text-gray-500"
            >
              Maybe Later
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============== Floating Banner ==============
export const NotificationBanner = ({ onEnabled }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Check if notifications are already enabled or banner was dismissed
    const hasToken = localStorage.getItem('fcm_token');
    const bannerDismissed = sessionStorage.getItem('notification_banner_dismissed');
    const permission = 'Notification' in window ? Notification.permission : 'denied';

    if (!hasToken && !bannerDismissed && permission !== 'granted' && permission !== 'denied') {
      // Show banner after a short delay
      const timer = setTimeout(() => setIsVisible(true), 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleEnable = async () => {
    setIsLoading(true);
    try {
      const token = await requestNotificationPermission();
      if (token) {
        await api.saveFcmToken(token);
        localStorage.setItem('fcm_token', token);
        toast({
          title: "Notifications Enabled!",
          description: "You'll now receive push notifications.",
        });
        setIsVisible(false);
        if (onEnabled) onEnabled();
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDismiss = () => {
    sessionStorage.setItem('notification_banner_dismissed', 'true');
    setIsDismissed(true);
    setTimeout(() => setIsVisible(false), 300);
  };

  if (!isVisible) return null;

  return (
    <div className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isDismissed ? 'opacity-0 -translate-y-full' : 'opacity-100 translate-y-0'}`}>
      <div className="bg-gradient-to-r from-[#0A2C93] to-[#1a4fd6] text-white px-4 py-3 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Bell className="h-5 w-5" />
            </div>
            <div>
              <p className="font-medium text-sm sm:text-base">Enable push notifications</p>
              <p className="text-white/70 text-xs sm:text-sm hidden sm:block">
                Get instant alerts for new bookings and payments
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={handleEnable}
              disabled={isLoading}
              className="bg-white text-[#0A2C93] hover:bg-white/90 font-medium"
            >
              {isLoading ? 'Enabling...' : 'Enable'}
            </Button>
            <button
              onClick={handleDismiss}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============== PWA Install Prompt ==============
export const usePWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showPostInstallPrompt, setShowPostInstallPrompt] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Listen for install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    // Listen for successful install
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      
      // Show notification prompt after PWA install
      const hasToken = localStorage.getItem('fcm_token');
      if (!hasToken) {
        setTimeout(() => {
          setShowPostInstallPrompt(true);
        }, 1500);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const promptInstall = async () => {
    if (!deferredPrompt) return false;
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    
    return outcome === 'accepted';
  };

  return {
    canInstall: !!deferredPrompt && !isInstalled,
    isInstalled,
    promptInstall,
    showPostInstallPrompt,
    closePostInstallPrompt: () => setShowPostInstallPrompt(false)
  };
};

// ============== Post-Install Notification Modal ==============
export const PostInstallNotificationModal = ({ isOpen, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleEnable = async () => {
    setIsLoading(true);
    try {
      const token = await requestNotificationPermission();
      if (token) {
        await api.saveFcmToken(token);
        localStorage.setItem('fcm_token', token);
        toast({
          title: "All Set!",
          description: "You'll receive notifications even when the app is closed.",
        });
        onClose(true);
      } else {
        onClose(false);
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md mx-4 overflow-hidden animate-in fade-in zoom-in duration-300">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#00D650] to-[#00b844] p-6 text-white">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
              <Smartphone className="h-8 w-8" />
            </div>
          </div>
          <h2 className="text-xl font-bold text-center">App Installed!</h2>
          <p className="text-white/90 text-center text-sm mt-1">
            One more step for the full experience
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-600 text-center mb-6">
            Enable notifications to get <strong>instant alerts</strong> for new bookings, 
            cancellations, and payments - even when the app is closed.
          </p>

          <div className="space-y-3">
            <Button
              onClick={handleEnable}
              disabled={isLoading}
              className="w-full bg-[#0A2C93] hover:bg-[#0A2C93]/90 text-white py-6"
            >
              <Bell className="h-5 w-5 mr-2" />
              {isLoading ? 'Enabling...' : 'Enable Notifications'}
            </Button>
            <Button
              onClick={() => onClose(false)}
              variant="ghost"
              className="w-full text-gray-500"
            >
              Skip for Now
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============== Combined Notification Manager ==============
const NotificationManager = () => {
  const [showFirstTimeModal, setShowFirstTimeModal] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const { showPostInstallPrompt, closePostInstallPrompt } = usePWAInstallPrompt();

  useEffect(() => {
    // Check if this is first login and notifications not enabled
    const hasToken = localStorage.getItem('fcm_token');
    const promptShown = localStorage.getItem('notification_prompt_shown');
    const permission = 'Notification' in window ? Notification.permission : 'denied';

    if (hasToken || permission === 'granted') {
      setNotificationsEnabled(true);
    } else if (!promptShown && permission !== 'denied') {
      // Show first-time modal after a short delay
      const timer = setTimeout(() => {
        setShowFirstTimeModal(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Listen for foreground messages
  useEffect(() => {
    const unsubscribe = onForegroundMessage((payload) => {
      toast({
        title: payload.notification?.title || 'New Notification',
        description: payload.notification?.body,
        duration: 8000,
      });
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);

  const handleFirstTimeModalClose = (enabled) => {
    setShowFirstTimeModal(false);
    if (enabled) setNotificationsEnabled(true);
  };

  const handlePostInstallClose = (enabled) => {
    closePostInstallPrompt();
    if (enabled) setNotificationsEnabled(true);
  };

  return (
    <>
      {/* First-time login modal */}
      <NotificationPromptModal 
        isOpen={showFirstTimeModal} 
        onClose={handleFirstTimeModalClose}
      />

      {/* Post PWA install modal */}
      <PostInstallNotificationModal
        isOpen={showPostInstallPrompt}
        onClose={handlePostInstallClose}
      />

      {/* Floating banner (shows if modal was skipped) */}
      {!notificationsEnabled && !showFirstTimeModal && !showPostInstallPrompt && (
        <NotificationBanner onEnabled={() => setNotificationsEnabled(true)} />
      )}
    </>
  );
};

export default NotificationManager;
